/* ══════════════════════════════════════════════════
 *  BulkReassignPanel
 *  Superadmin panel to reassign tasks in bulk from one user
 *  to one or more users with optional filters and dry-run preview.
 *  Used both inside the Tasks page (modal) and the Admin section.
 * ══════════════════════════════════════════════════ */
import { useMemo, useState } from 'react';
import { useSolicitors } from '../../hooks/useCaseFile';
import { useCaseTypes, useStages } from '../../hooks/useCases';
import {
  useAdvancedReassign,
  type AdvancedReassignFilters,
  type AdvancedReassignResult,
} from '../../hooks/useHome';
import SearchableMultiSelect from '../ui/SearchableMultiSelect/SearchableMultiSelect';
import css from './BulkReassignPanel.module.css';

const CATEGORIES = [
  { value: '', label: 'Any category' },
  { value: 'admin', label: 'Admin' },
  { value: 'client', label: 'Client' },
  { value: 'court', label: 'Court' },
  { value: 'defendant', label: 'Defendant' },
  { value: 'misc', label: 'Miscellaneous' },
];

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Overdue'];

interface Props {
  /** Optional callback invoked after a real (non-dry-run) reassign succeeds */
  onReassignComplete?: (result: AdvancedReassignResult) => void;
}

export default function BulkReassignPanel({ onReassignComplete }: Props) {
  const { data: solicitors = [] } = useSolicitors();
  const { data: caseTypes = [] } = useCaseTypes();

  const [fromUserId, setFromUserId] = useState('');
  const [toUserIds, setToUserIds] = useState<string[]>([]);
  const [caseTypeId, setCaseTypeId] = useState<number | null>(null);
  const [stageId, setStageId] = useState<number | null>(null);
  const [category, setCategory] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]); // empty = all (excludes completed)
  const [deadlineFrom, setDeadlineFrom] = useState('');
  const [deadlineTo, setDeadlineTo] = useState('');

  const [preview, setPreview] = useState<AdvancedReassignResult | null>(null);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const selectedCaseTypeName = useMemo(
    () => caseTypes.find((c) => c.id === caseTypeId)?.name ?? null,
    [caseTypes, caseTypeId],
  );
  const { data: stages = [] } = useStages(selectedCaseTypeName);

  const reassign = useAdvancedReassign();

  const buildFilters = (): AdvancedReassignFilters => ({
    caseTypeId: caseTypeId ?? null,
    stageId: stageId ?? null,
    category: category || null,
    statuses: statuses.length > 0 ? statuses : undefined,
    deadlineFrom: deadlineFrom || null,
    deadlineTo: deadlineTo || null,
  });

  const validate = (requireTargets: boolean): string | null => {
    if (!fromUserId) return 'Choose a "From" user';
    if (requireTargets && toUserIds.length === 0) return 'Choose at least one "To" user';
    if (requireTargets && toUserIds.includes(fromUserId)) {
      return '"To" users cannot include the "From" user';
    }
    return null;
  };

  const handlePreview = async () => {
    setErrorMsg(null);
    setResultMsg(null);
    const err = validate(false);
    if (err) {
      setErrorMsg(err);
      return;
    }
    try {
      const result = await reassign.mutateAsync({
        fromUserId,
        // Backend requires at least one toUserId, but for preview the value is unused.
        toUserIds: toUserIds.length > 0 ? toUserIds : [fromUserId],
        filters: buildFilters(),
        dryRun: true,
      });
      setPreview(result);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Preview failed');
    }
  };

  const handleReassign = async () => {
    setErrorMsg(null);
    setResultMsg(null);
    const err = validate(true);
    if (err) {
      setErrorMsg(err);
      return;
    }
    if (!preview || preview.count === 0) {
      setErrorMsg('Run "Preview" first to confirm matching tasks');
      return;
    }
    if (
      !window.confirm(
        `Reassign ${preview.count} task(s) from the selected user to ${toUserIds.length} target(s)?`,
      )
    ) {
      return;
    }
    try {
      const result = await reassign.mutateAsync({
        fromUserId,
        toUserIds,
        filters: buildFilters(),
        dryRun: false,
      });
      setResultMsg(`Reassigned ${result.reassigned} task(s).`);
      setPreview(null);
      onReassignComplete?.(result);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Reassign failed');
    }
  };

  const toggleStatus = (s: string) => {
    setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    setPreview(null);
  };

  return (
    <div className={css.panel}>
      {/* From / To */}
      <div className={css.row}>
        <div className={css.field}>
          <label className={css.label}>From user</label>
          <select
            className={css.select}
            value={fromUserId}
            onChange={(e) => {
              setFromUserId(e.target.value);
              setPreview(null);
            }}
          >
            <option value="">Select user…</option>
            {solicitors.map((s) => (
              <option key={s.cognito_user_id} value={s.cognito_user_id}>
                {s.displayName || s.username}
              </option>
            ))}
          </select>
        </div>
        <div className={css.field}>
          <label className={css.label}>
            To users {toUserIds.length > 1 && <span>(round-robin distribution)</span>}
          </label>
          <SearchableMultiSelect
            options={solicitors
              .filter((s) => s.cognito_user_id !== fromUserId)
              .map((s) => ({
                value: s.cognito_user_id,
                label: s.displayName || s.username,
              }))}
            value={toUserIds}
            onChange={(ids) => {
              setToUserIds(ids);
              setPreview(null);
            }}
            placeholder="Select target users…"
          />
          {toUserIds.length > 1 && (
            <div className={css.help}>
              Tasks will be split round-robin across {toUserIds.length} users.
            </div>
          )}
        </div>
      </div>

      {/* Case Type / Stage */}
      <div className={css.row}>
        <div className={css.field}>
          <label className={css.label}>Pipeline / Case type</label>
          <select
            className={css.select}
            value={caseTypeId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setCaseTypeId(v ? Number(v) : null);
              setStageId(null);
              setPreview(null);
            }}
          >
            <option value="">Any pipeline</option>
            {caseTypes.map((ct) => (
              <option key={ct.id} value={ct.id}>
                {ct.display_name || ct.name}
              </option>
            ))}
          </select>
        </div>
        <div className={css.field}>
          <label className={css.label}>Stage</label>
          <select
            className={css.select}
            value={stageId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setStageId(v ? Number(v) : null);
              setPreview(null);
            }}
            disabled={!caseTypeId}
          >
            <option value="">Any stage</option>
            {stages.map((st) => (
              <option key={st.stage_id} value={st.stage_id}>
                {st.stage_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Category / Date range */}
      <div className={css.row}>
        <div className={css.field}>
          <label className={css.label}>Category</label>
          <select
            className={css.select}
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPreview(null);
            }}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className={css.field}>
          <label className={css.label}>Deadline range</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="date"
              className={css.input}
              value={deadlineFrom}
              onChange={(e) => {
                setDeadlineFrom(e.target.value);
                setPreview(null);
              }}
            />
            <input
              type="date"
              className={css.input}
              value={deadlineTo}
              onChange={(e) => {
                setDeadlineTo(e.target.value);
                setPreview(null);
              }}
            />
          </div>
        </div>
      </div>

      {/* Status chips */}
      <div className={css.field}>
        <label className={css.label}>Status (default: all non-completed)</label>
        <div className={css.statusGroup}>
          {STATUS_OPTIONS.map((s) => {
            const active = statuses.includes(s);
            return (
              <button
                key={s}
                type="button"
                className={`${css.statusChip} ${active ? css.statusChipActive : ''}`}
                onClick={() => toggleStatus(s)}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Messages */}
      {errorMsg && <div className={css.error}>{errorMsg}</div>}
      {resultMsg && <div className={css.success}>{resultMsg}</div>}

      {/* Action buttons */}
      <div className={css.actions}>
        <button
          type="button"
          className={css.btn}
          onClick={handlePreview}
          disabled={reassign.isPending || !fromUserId}
        >
          {reassign.isPending && reassign.variables?.dryRun ? 'Loading…' : 'Preview matches'}
        </button>
        <button
          type="button"
          className={`${css.btn} ${css.btnPrimary}`}
          onClick={handleReassign}
          disabled={reassign.isPending || !preview || preview.count === 0 || toUserIds.length === 0}
        >
          {reassign.isPending && !reassign.variables?.dryRun
            ? 'Reassigning…'
            : `Reassign${preview ? ` ${preview.count}` : ''}`}
        </button>
      </div>

      {/* Preview */}
      {preview && (
        <div className={css.previewBox}>
          <div className={css.previewHeader}>
            <span>
              Preview ({preview.count} task{preview.count === 1 ? '' : 's'} match)
            </span>
            {preview.count > preview.sample.length && (
              <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted)' }}>
                Showing first {preview.sample.length}
              </span>
            )}
          </div>
          {preview.count === 0 ? (
            <div className={css.empty}>No matching tasks.</div>
          ) : (
            <table className={css.previewTable}>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Case Ref</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Deadline</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((t) => (
                  <tr key={t.TaskID}>
                    <td>{t.Description ?? '—'}</td>
                    <td>{t.CaseRef ?? '—'}</td>
                    <td>{t.Category ?? '—'}</td>
                    <td>{t.Status ?? '—'}</td>
                    <td>{t.Deadline ? new Date(t.Deadline).toISOString().slice(0, 10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {toUserIds.length > 1 && preview.count > 0 && (
            <div className={css.distribution}>
              Will distribute round-robin: ~{Math.ceil(preview.count / toUserIds.length)} task(s)
              per user across {toUserIds.length} target(s).
            </div>
          )}
        </div>
      )}
    </div>
  );
}
